import { useState } from "react";
import { ApiError, type CalendarChoiceView, type CalendarConnectionView } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";

/** KEY-821: "football, school run" → ["football", "school run"]. */
export function parseKeywords(text: string): string[] {
  return text.split(",").map((w) => w.trim()).filter((w) => w.length > 0);
}

/** KEY-819: where an account's new appointments go, in words. */
export function writeLine(c: Pick<CalendarConnectionView, "writeAllowed" | "writeCalendarName">): string {
  if (!c.writeAllowed) return "Dials can't add appointments to it.";
  return `Dials add appointments to ${c.writeCalendarName ?? "its main calendar"}.`;
}

/**
 * KEY-819: letting dials add appointments to one account, and which of its calendars they go into. Off by default —
 * connecting only ever reads. Google and Outlook are asked again, on their own consent screen, for the wider
 * permission (a full-page round trip back to Your settings); CalDAV's app password can already write, so it is on at
 * once. Stopping is immediate here; the permission stays granted at the provider until the person revokes it there.
 */
export function CalendarWriteControls({
  connection,
  field,
  onChanged,
}: {
  connection: CalendarConnectionView;
  field: React.CSSProperties;
  onChanged: (note: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choices, setChoices] = useState<CalendarChoiceView[] | null>(null);
  const [into, setInto] = useState<string>("");
  const savedWords = (connection.keywords ?? []).join(", ");
  const [words, setWords] = useState(savedWords);

  const run = async (work: (token: string) => Promise<void>) => {
    const token = getAccessToken();
    if (!token || busy) return;
    setBusy(true);
    setError(null);
    try {
      await work(token);
    } catch (e) {
      setError(e instanceof ApiError && e.message ? e.message : "That couldn't be changed just now.");
    } finally {
      setBusy(false);
    }
  };

  const allow = () =>
    run(async (token) => {
      const started = await api.allowCalendarWrites(token, connection.id);
      if (started.authorizationUrl) {
        // As for connecting: replace, so Back from the page we return to doesn't land on the provider's screen.
        window.location.replace(started.authorizationUrl);
        return;
      }
      onChanged(started.connection ? writeLine(started.connection) : "Dials can now add appointments.");
    });

  const stop = () =>
    run(async (token) => {
      await api.stopCalendarWrites(token, connection.id);
      onChanged("Dials won't add appointments to that account any more. What they added is still in the calendar.");
    });

  const change = () =>
    run(async (token) => {
      const list = await api.calendarChoices(token, connection.id);
      const writable = list.calendars.filter((c) => c.canWrite);
      setChoices(writable);
      setInto(writable.find((c) => c.writesHere)?.id ?? writable[0]?.id ?? "");
    });

  const saveWords = () =>
    run(async (token) => {
      const view = await api.setCalendarKeywords(token, connection.id, parseKeywords(words));
      setWords((view.keywords ?? []).join(", "));
      onChanged(view.keywords?.length ? `Milo will put appointments mentioning ${view.keywords.join(", ")} in ${connection.accountLabel}.` : "Milo has no words for that calendar now.");
    });

  const save = () =>
    run(async (token) => {
      const view = await api.setWriteCalendar(token, connection.id, into);
      setChoices(null);
      onChanged(writeLine(view));
    });

  return (
    <div style={{ flexBasis: "100%", display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "var(--text-muted)" }}>
        <span>{writeLine(connection)}</span>
        {connection.writeAllowed ? (
          <>
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void change()}>Change calendar</button>
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void stop()}>Stop</button>
          </>
        ) : (
          <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void allow()}>
            Let dials add appointments
          </button>
        )}
      </div>
      {choices && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {choices.length === 0 ? (
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>None of this account's calendars can be written to.</span>
          ) : (
            <select aria-label="Calendar for new appointments" value={into} onChange={(e) => setInto(e.target.value)} style={field}>
              {choices.map((c) => (
                <option key={c.id} value={c.id}>{c.isPrimary ? `${c.name} (main)` : c.name}</option>
              ))}
            </select>
          )}
          {choices.length > 0 && (
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy || !into} onClick={() => void save()}>Save</button>
          )}
          <button type="button" className="kd-btn kd-btn-sm" onClick={() => setChoices(null)}>Cancel</button>
        </div>
      )}
      {connection.writeAllowed && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "var(--text-muted)" }}>
          <label htmlFor={`words-${connection.id}`}>Milo puts appointments mentioning</label>
          <input
            id={`words-${connection.id}`}
            value={words}
            maxLength={600}
            placeholder="e.g. football, school run"
            onChange={(e) => setWords(e.target.value)}
            style={{ ...field, flex: "1 1 180px", fontSize: 13 }}
          />
          <span>here</span>
          {words.trim() !== savedWords && (
            <button type="button" className="kd-btn kd-btn-sm" disabled={busy} onClick={() => void saveWords()}>Save words</button>
          )}
        </div>
      )}
      {error && <div role="alert" style={{ fontSize: 13, color: "var(--danger, #b42318)" }}>{error}</div>}
    </div>
  );
}
